import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { Button } from '../components/ui/button';
import {
  Sparkles,
  ShieldCheck,
  Lock,
  BadgeCheck,
  CreditCard,
  Globe,
  Receipt,
  Crown,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import { PricingPlanCard } from '../components/billing/PricingPlanCard';
import { GuestCheckoutDialog } from '../components/billing/GuestCheckoutDialog';
import QuarterlyLeadDialog from '../components/landing/QuarterlyLeadDialog';
import {
  billingApi,
  detectCurrency,
  fetchServerCurrency,
  cachedGeoCountry,
  persistCurrency,
  hasPendingAudit,
  openOneTimeCheckout,
} from '../lib/billing';
import { useAuth } from '../lib/auth-context';
import SeoHead from '../components/seo/SeoHead';
import { trackCta } from '../lib/analytics';

// Feb-2026 pivot — Pricing page shows TWO plans:
//   1. Full Profile Makeover  (₹999 / $49 · one-time)  → Razorpay
//   2. Executive Leadership Quarterly (₹48,000 / $1,490 · 90 days)
//      → lead form (QuarterlyLeadDialog), no self-serve checkout.

const FAQ = [
  {
    q: 'How does the Full Profile Makeover work?',
    a: 'A one-time \u20b9999 (~$49) charge. You get 15 days of login access from purchase, during which you can upload ONE LinkedIn PDF and analyse it up to 3 times, generate up to 10 thought-leadership posts (all types combined), get rewritten Headline / About / Experience copy, a peer benchmark deep-dive, and premium branded PDF + PPTX downloads. No subscription, no auto-renew.',
  },
  {
    q: 'What is the Executive Leadership Quarterly plan?',
    a: 'A 90-day, fully-managed executive presence engagement. A dedicated strategist runs your LinkedIn narrative end-to-end \u2014 weekly researched posts, designed carousels, comparative positioning vs top peers, connection building automation, 1:1 messaging cadence, and a monthly working session with a senior expert. Charged quarterly at \u20b948,000 / $1,490.',
  },
  {
    q: 'How fast do I get access after paying?',
    a: 'Instant. On successful payment, your account is auto-created using the email you paid with and a one-click magic-login link + fallback credentials land in your inbox within seconds. Click the link, tap through, run your first audit.',
  },
  {
    q: 'Can I upgrade from the Makeover to the Quarterly plan later?',
    a: 'Yes. Anything you paid for the Makeover in the last 30 days is credited towards the first month of the Quarterly engagement. Just mention it on the discovery call \u2014 we\u2019ll handle the arithmetic.',
  },
  {
    q: 'Do I need to upload a LinkedIn PDF?',
    a: 'No. Once you\u2019re in, paste your LinkedIn profile URL and the app pulls the profile + recent posts directly so the AI learns your real voice without you exporting anything. PDF upload still works as a fallback.',
  },
  {
    q: 'Do you support GST invoices?',
    a: 'Yes \u2014 GST invoices are available on request for both plans. Reply to your purchase confirmation email with your GSTIN and company address, and we\u2019ll email a compliant invoice within one business day.',
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [plansError, setPlansError] = useState(null); // 'network' | 'empty' | null
  const [plansRetryTick, setPlansRetryTick] = useState(0);

  // URL override: /pricing?currency=USD (or INR) — cross-region testing escape hatch.
  const currencyOverride = useMemo(() => {
    const raw = (searchParams.get('currency') || '').toUpperCase();
    return raw === 'INR' || raw === 'USD' ? raw : null;
  }, [searchParams]);

  const [currency, setCurrency] = useState(currencyOverride || detectCurrency());
  const [country, setCountry] = useState(currencyOverride ? '' : cachedGeoCountry());

  useEffect(() => {
    if (currencyOverride) persistCurrency(currencyOverride);
  }, [currencyOverride]);

  const [loadingPlan, setLoadingPlan] = useState(null);
  const [guestCheckoutOpen, setGuestCheckoutOpen] = useState(false);
  const [quarterlyDialogOpen, setQuarterlyDialogOpen] = useState(false);
  const [me, setMe] = useState(null);
  const auditQuotaReturn = searchParams.get('reason') === 'audit-quota';

  useEffect(() => {
    let active = true;
    setPlansError(null);
    // Client-side fallback catalog — used only when /api/billing/plans is
    // unreachable. Kept in sync with billing_mod.PUBLIC_PLAN_CATALOG.
    const FALLBACK_PLANS = [
      {
        key: 'one_time',
        label: 'Full Profile Makeover',
        tagline: 'Complete LinkedIn overhaul in 10 minutes — audit, rewrite, and a deep-dive report you can flaunt.',
        price_inr: 999,
        price_usd: 49,
        billing: 'one_time',
        validity_days: 15,
        features: [
          'Complete profile audit \u2014 analyse your uploaded PDF up to 3 times',
          'Rewritten Headline, About & Experience copy tuned to your voice',
          'Create up to 10 thought-leadership LinkedIn posts (all types combined)',
          'Premium Resume Makeover (Includes Design & Download)',
          'Peer benchmark deep-dive + branded PDF & PPTX reports',
        ],
      },
      {
        key: 'quarterly_leadership',
        label: 'Executive Leadership Quarterly',
        tagline: 'A 90-day, fully-managed executive presence engagement — expert-led, results-owned.',
        price_inr: 48000,
        price_usd: 1490,
        billing: 'quarterly',
        validity_days: 90,
        features: [
          'Dedicated executive brand strategist for 90 days',
          'Weekly researched posts + carousels, written & designed for you',
          'Premium Resume Makeover (Includes Design & Download)',
          'Comparative positioning + gap analysis vs top industry peers',
          '1:1 monthly working sessions with a senior expert',
          'Connection building automation + 1:1 messaging cadence',
          'Granular monthly reporting on reach, engagement & voice growth',
        ],
        lead_form: true,
      },
    ];

    billingApi
      .plans()
      .then((d) => {
        if (!active) return;
        const arr = Array.isArray(d?.plans) ? d.plans : [];
        if (arr.length === 0) {
          setPlans(FALLBACK_PLANS);
          setPlansError('empty');
        } else {
          setPlans(arr);
        }
      })
      .catch((err) => {
        if (!active) return;
        // eslint-disable-next-line no-console
        console.error('[pricing] /api/billing/plans failed:', err?.message || err);
        setPlans(FALLBACK_PLANS);
        setPlansError('network');
      });
    if (user) {
      billingApi.me().then((d) => {
        if (active) setMe(d);
      }).catch(() => {});
    }
    if (!currencyOverride) {
      fetchServerCurrency().then((geo) => {
        if (!active || !geo) return;
        setCountry(geo.country || '');
        if (geo.currency && geo.currency !== currency) setCurrency(geo.currency);
      });
    }
    return () => {
      active = false;
    };
  }, [user, plansRetryTick, currencyOverride]);

  const profileMakeover = useMemo(
    () => plans.find((p) => p.key === 'one_time'),
    [plans],
  );
  const quarterly = useMemo(
    () => plans.find((p) => p.key === 'quarterly_leadership'),
    [plans],
  );

  const handleCurrency = (v) => {
    if (!v) return;
    setCurrency(v);
    persistCurrency(v);
  };

  const onMakeoverCheckout = async () => {
    trackCta('pricing_plan_click', {
      plan: 'one_time',
      currency,
      logged_in: !!user,
    });
    if (!user) {
      setGuestCheckoutOpen(true);
      return;
    }
    const audit_next = hasPendingAudit();
    const goSuccess = (result) => {
      window.dispatchEvent(new Event('billing:refresh'));
      const cur = String(result?.currency || currency || 'USD').toUpperCase();
      const region = cur === 'INR' ? 'in' : 'us';
      const amt = result?.amount_paise ? Number(result.amount_paise) / 100 : undefined;
      const params = new URLSearchParams();
      if (result?.plan) params.set('plan', result.plan);
      if (cur) params.set('currency', cur);
      if (amt != null) params.set('amount', String(amt));
      if (result?.order_id) params.set('order_id', result.order_id);
      if (result?.payment_id) params.set('payment_id', result.payment_id);
      if (audit_next) params.set('next', 'audit');
      navigate(`/thank-you/${region}?${params.toString()}`);
    };
    setLoadingPlan('one_time');
    try {
      await openOneTimeCheckout({
        ctaLabel: 'pricing_page_buy_now',
        onSuccess: goSuccess,
        onDismiss: () => setLoadingPlan(null),
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const priceLabelMakeover =
    currency === 'INR' ? '\u20b9999' : '$49';

  return (
    <AppShell>
      <SeoHead
        title={'Pricing \u2014 Blayzrr'}
        description={
          'Full Profile Makeover \u2014 \u20b9999 / $49 one-time. Executive Leadership Quarterly \u2014 \u20b948,000 / $1,490 for 90 days, fully managed.'
        }
        path={'/pricing'}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <header className="space-y-3">
          <p
            className="font-mono-edge text-xs tracking-[0.18em] uppercase text-muted-foreground"
            data-testid="pricing-hero-kicker"
          >
            BILLING · PRICING
          </p>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]"
            data-testid="pricing-hero-headline"
          >
            Two ways to become the LinkedIn leader in your space.
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl">
            Ship a fully rewritten profile in an evening — or hand the entire quarter to
            our strategists and watch the reach compound.
          </p>
          <div
            className="flex flex-wrap items-center gap-2 pt-3"
            data-testid="pricing-hero-trust-row"
          >
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-1 text-xs font-medium text-[hsl(var(--success))]"
              data-testid="trust-chip-razorpay"
            >
              <ShieldCheck size={13} strokeWidth={2.4} />
              Razorpay Secure Checkout
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground"
              data-testid="trust-chip-no-autopay"
            >
              <BadgeCheck size={13} strokeWidth={2.4} />
              No Auto-pay
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground"
              data-testid="trust-chip-encryption"
            >
              <Lock size={13} strokeWidth={2.4} />
              256-bit SSL encryption
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground"
              data-testid="trust-chip-international"
            >
              <Globe size={13} strokeWidth={2.4} />
              International cards accepted
            </span>
          </div>
        </header>

        {auditQuotaReturn && (
          <div
            className="mt-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-[hsl(var(--mint))]/30 via-card to-[hsl(var(--sky))]/20 p-4 sm:p-5 flex items-start gap-3"
            data-testid="pricing-audit-quota-banner"
          >
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
              <Sparkles size={16} />
            </span>
            <div className="min-w-0">
              <Badge
                variant="secondary"
                className="font-mono-edge text-[10px] tracking-[0.18em]"
              >
                ALMOST THERE
              </Badge>
              <p className="font-semibold mt-1.5 text-sm sm:text-base">
                Pick a plan to run your audit.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                We saved your profile details. Once you pay, we&apos;ll bring you
                straight back and start the audit &mdash; no re-uploads.
              </p>
            </div>
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">
              {currencyOverride ? (
                <>
                  Currency override: <span className="font-medium text-foreground">{currency}</span>{' '}
                  (via <code className="font-mono-edge text-[11px]">?currency={currency}</code>).
                </>
              ) : country === 'IN' ? (
                <>
                  Prices shown in <span className="font-medium text-foreground">INR</span> for India.
                </>
              ) : country && country !== 'IN' ? (
                <>
                  Prices shown in <span className="font-medium text-foreground">USD</span> for your region.
                </>
              ) : (
                <>
                  Auto-detected: <span className="font-medium text-foreground">{currency}</span> &middot; change
                </>
              )}
            </p>
            {currency === 'USD' && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="pricing-usd-note">
                Charged in USD via Razorpay International. Card statement shows the USD amount directly.
              </p>
            )}
          </div>
          {(!country || currencyOverride) && (
            <ToggleGroup
              type="single"
              value={currency}
              onValueChange={handleCurrency}
              variant="outline"
              data-testid="pricing-currency-toggle"
            >
              <ToggleGroupItem value="INR" data-testid="pricing-currency-inr">
                INR
              </ToggleGroupItem>
              <ToggleGroupItem value="USD" data-testid="pricing-currency-usd">
                USD
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>

        {plansError && (
          <div
            className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-3 py-2.5 text-[13px] text-foreground/85 flex items-center gap-3 flex-wrap"
            data-testid="pricing-plans-fallback-banner"
          >
            <span className="flex-1">
              We couldn&apos;t load the live pricing catalog right now &mdash; showing
              the standard prices. Live prices &amp; checkout still work; the banner
              will clear once we reconnect.
            </span>
            <button
              type="button"
              onClick={() => setPlansRetryTick((t) => t + 1)}
              className="text-primary underline underline-offset-4 text-xs font-medium hover:opacity-80"
              data-testid="pricing-plans-fallback-retry"
            >
              Retry
            </button>
          </div>
        )}

        <div
          className="mt-6 grid md:grid-cols-2 gap-4 lg:gap-6 items-stretch"
          data-testid="pricing-plans-grid"
        >
          {profileMakeover && (
            <PricingPlanCard
              plan={profileMakeover}
              currency={currency}
              recommended
              currentPlanKey={me?.plan_type}
              ctaLabel="Buy Now"
              loading={loadingPlan === 'one_time'}
              onCta={onMakeoverCheckout}
              onGuestCheckout={() => setGuestCheckoutOpen(true)}
              testId="pricing-plan-profile-makeover-card"
            />
          )}
          {quarterly && (
            <QuarterlyPlanCard
              plan={quarterly}
              currency={currency}
              onLead={() => {
                trackCta('pricing_quarterly_enquire');
                setQuarterlyDialogOpen(true);
              }}
            />
          )}
          {plans.length === 0 && (
            <>
              <Card>
                <CardContent className="p-7 space-y-3 animate-pulse">
                  <div className="h-7 w-40 bg-secondary rounded" />
                  <div className="h-12 w-24 bg-secondary rounded" />
                  <div className="h-3 w-full bg-secondary rounded" />
                  <div className="h-3 w-3/4 bg-secondary rounded" />
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-7 space-y-3 animate-pulse">
                  <div className="h-7 w-40 bg-secondary rounded" />
                  <div className="h-12 w-24 bg-secondary rounded" />
                  <div className="h-3 w-full bg-secondary rounded" />
                  <div className="h-3 w-3/4 bg-secondary rounded" />
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Trust pillars */}
        <section className="mt-12" data-testid="pricing-trust-pillars">
          <div className="rounded-2xl border border-border bg-card/50 p-5 sm:p-7">
            <div className="mb-5">
              <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                WHY IT&apos;S SAFE TO START
              </p>
              <h3 className="font-display text-xl sm:text-2xl tracking-[-0.01em] mt-1">
                Zero-risk to try. Built on standards you already trust.
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <TrustCard
                Icon={ShieldCheck}
                title="Razorpay secure"
                desc="PCI-DSS Level 1 gateway used by 10M+ Indian businesses."
                iconBg="bg-primary/10"
                iconColor="text-primary"
                testid="trust-pillar-razorpay"
              />
              <TrustCard
                Icon={BadgeCheck}
                title="No auto-renew"
                desc="One-time charge, one-time only. Nothing hits your card again."
                iconBg="bg-[hsl(var(--sky))]/30"
                iconColor="text-primary"
                testid="trust-pillar-cancel"
              />
              <TrustCard
                Icon={Lock}
                title="256-bit encryption"
                desc="Your card never touches our servers. End-to-end SSL on every page."
                iconBg="bg-secondary"
                iconColor="text-foreground"
                testid="trust-pillar-encryption"
              />
              <TrustCard
                Icon={Receipt}
                title="No hidden fees"
                desc="The price you see is the price you pay. GST invoice on request."
                iconBg="bg-[hsl(var(--peach))]/30"
                iconColor="text-foreground"
                testid="trust-pillar-no-hidden-fees"
              />
              <TrustCard
                Icon={Globe}
                title="Global cards welcome"
                desc="Visa, Mastercard, Amex, UPI, Net banking, and most international cards."
                iconBg="bg-secondary"
                iconColor="text-foreground"
                testid="trust-pillar-international"
              />
              <TrustCard
                Icon={CreditCard}
                title="Instant access"
                desc="Pay and a one-click login link lands in your inbox within seconds."
                iconBg="bg-[hsl(var(--mint))]/30"
                iconColor="text-[hsl(var(--success))]"
                testid="trust-pillar-instant"
              />
            </div>
          </div>
        </section>

        <section className="mt-14">
          <p className="font-mono-edge text-xs tracking-[0.18em] uppercase text-muted-foreground">
            FAQ
          </p>
          <h2 className="font-display text-2xl sm:text-3xl tracking-[-0.01em] mt-1">
            Common questions
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-4 grid lg:grid-cols-2 gap-x-8"
            data-testid="pricing-faq"
          >
            {FAQ.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                <AccordionTrigger
                  className="text-left font-medium"
                  data-testid={`pricing-faq-q-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <footer
          className="border-t border-border mt-12 pt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground"
          data-testid="pricing-footer"
        >
          <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase">© Blayzrr</p>
          <div className="flex items-center gap-4">
            <a
              href="/refund-policy"
              className="hover:text-foreground"
              data-testid="pricing-footer-refund-link"
            >
              Refund Policy
            </a>
            <a href="/methodology" className="hover:text-foreground">
              Methodology
            </a>
          </div>
        </footer>
      </div>

      <GuestCheckoutDialog
        open={guestCheckoutOpen}
        onOpenChange={setGuestCheckoutOpen}
        planLabel="Full Profile Makeover"
        priceLabel={priceLabelMakeover}
      />
      <QuarterlyLeadDialog
        open={quarterlyDialogOpen}
        onOpenChange={setQuarterlyDialogOpen}
      />
    </AppShell>
  );
}

const TrustCard = ({ Icon, title, desc, iconBg, iconColor, testid }) => (
  <div
    className="rounded-xl border border-border bg-background p-4"
    data-testid={testid}
  >
    <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${iconBg} ${iconColor}`}>
      <Icon size={18} strokeWidth={2.2} />
    </div>
    <p className="font-semibold mt-3 text-sm">{title}</p>
    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{desc}</p>
  </div>
);

/** Premium, "talk to us" card for the Executive Leadership Quarterly plan. */
const QuarterlyPlanCard = ({ plan, currency, onLead }) => {
  const price =
    currency === 'INR' ? plan.price_inr : plan.price_usd;
  const priceLabel =
    currency === 'INR'
      ? `\u20b9${(price || 0).toLocaleString('en-IN')}`
      : `$${(price || 0).toLocaleString('en-US')}`;
  return (
    <Card
      className="relative rounded-2xl border border-foreground/15 bg-foreground text-background h-full"
      data-testid="pricing-plan-quarterly-card"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl"
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(500px circle at 85% 15%, hsl(212 92% 55% / 0.35), transparent 55%), radial-gradient(400px circle at 10% 90%, hsl(205 85% 55% / 0.2), transparent 60%)',
          }}
        />
      </div>
      <Badge className="absolute -top-3 right-7 rounded-full text-[10px] tracking-[0.22em] font-mono uppercase px-3 py-1 bg-background text-foreground whitespace-nowrap z-10">
        <Crown size={11} className="mr-1" aria-hidden="true" /> Fully Managed
      </Badge>
      <CardContent className="relative p-6 sm:p-7 flex flex-col h-full">
        <p className="font-mono-edge text-[10px] tracking-[0.18em] uppercase text-background/70">
          QUARTERLY · 90 DAYS
        </p>
        <h3
          className="font-display text-2xl mt-1 leading-tight text-background"
          data-testid="pricing-plan-quarterly-card-name"
        >
          {plan.label}
        </h3>
        {plan.tagline && (
          <p className="text-sm text-background/75 mt-1">{plan.tagline}</p>
        )}

        <div className="mt-6 border-t border-background/20 pt-5">
          <span className="font-mono-edge text-[10px] tracking-[0.18em] text-background/60">
            {currency}
          </span>
          <div
            className="flex items-baseline gap-2 mt-1"
            data-testid="pricing-plan-quarterly-card-price"
          >
            <div className="text-4xl sm:text-5xl font-semibold tracking-[-0.02em] leading-none text-background tabular-nums">
              {priceLabel}
            </div>
            <span className="text-sm text-background/70">/ quarter</span>
          </div>
          <p className="text-xs text-background/70 mt-2">
            Billed once every 90 days · Discovery call before we start
          </p>
        </div>

        <ul
          className="mt-6 space-y-2.5 text-sm"
          data-testid="pricing-plan-quarterly-card-features"
        >
          {plan.features?.map((f, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 text-background/90"
              data-testid={`pricing-plan-quarterly-card-feature-${i}`}
            >
              <CheckCircle2
                size={14}
                className="mt-0.5 shrink-0 text-[hsl(212_92%_75%)]"
                aria-hidden="true"
              />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div
          className="mt-auto pt-5 border-t border-background/20 flex flex-col justify-center space-y-3"
          data-testid="pricing-plan-quarterly-card-cta-block"
        >
          <Button
            onClick={onLead}
            className="w-full h-11 rounded-full bg-background text-foreground hover:bg-background/90"
            data-testid="pricing-plan-quarterly-card-cta"
          >
            Request a strategy call
            <ArrowRight size={14} className="ml-2" aria-hidden="true" />
          </Button>
          <p className="text-[11px] text-background/70 text-center">
            A senior strategist will reach out within one business day.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
